import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_BASE } from "@/lib/api";
import { Sparkles, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("Missing reset token. Try requesting a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don&apos;t match."); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Reset failed. Try again.");
        return;
      }
      setDone(true);
      toast.success("Password updated — taking you to sign in");
      setTimeout(() => nav("/admin"), 1500);
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#183D36] mb-6">
          <ArrowLeft size={14} /> Back to sign in
        </Link>
        <div className="rounded-3xl bg-white border border-[#E5E0D8] p-8 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-9 h-9 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
              <Sparkles size={16} />
            </span>
            <h1 className="font-heading text-2xl text-[#183D36]">Set a new password</h1>
          </div>
          {done ? (
            <div data-testid="reset-success">
              <div className="w-14 h-14 rounded-2xl bg-[#A8BFA3] flex items-center justify-center mb-3">
                <CheckCircle2 className="text-[#183D36]" size={22} />
              </div>
              <p className="text-[#252525] font-medium mb-2">Password updated.</p>
              <p className="text-sm text-[#4A4A4A]">Redirecting you to the sign-in page…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="rp-pw">New password</Label>
                <Input
                  id="rp-pw"
                  data-testid="reset-input-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rp-confirm">Confirm password</Label>
                <Input
                  id="rp-confirm"
                  data-testid="reset-input-confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter the same password"
                  required
                />
              </div>
              {error && (
                <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl p-3" data-testid="reset-error">
                  <AlertCircle size={14} className="mt-0.5 flex-none" />
                  <span>{error}</span>
                </div>
              )}
              <Button
                type="submit"
                disabled={busy || !token}
                data-testid="reset-submit"
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0]"
              >
                {busy ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
